'use client';

import React, { ComponentPropsWithoutRef, ReactNode, useState } from 'react';

import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';

import { AnimatedText } from '@/blocks/animations/animated-text';
import { useTabContentVariants } from '@/blocks/animations/useTabContentVariants';
import { Container } from '@/blocks/components/Container';
import { DeprecatedTabPanel, DeprecatedTabs } from '@/blocks/components/DeprecatedTabs';
import { Disclaimer } from '@/blocks/components/Disclaimer';
import { AccentFragment } from '@/blocks/components/HeadingAccentFragment';
import { ArrowRight, IconGlobe, IconMetatrader5 } from '@/blocks/components/Icons';
import { MT5Link } from '@/blocks/components/MT5OfferingModal/Modal';
import { LinkSignup } from '@/blocks/components/PlatformLink';
import { CenterHeading, VStack } from '@/blocks/components/layout';
import { MT5_VIA_PXBT } from '@/global/public-settings';
import { LinkI18n } from '@/i18n/routing';
import { EventBlockNames } from '@/providers/analytics/types';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';

import metatraderImageX1 from './metatrader.png';
import metatraderImageX2 from './metatrader@2x.png';
import styles from './styles.module.scss';
import webtraderImageX1 from './webtrader.png';
import webtraderImageX2 from './webtrader@2x.png';

enum Platform {
  WT = 1,
  MT5 = 2,
}

interface TabImageProps extends ComponentPropsWithoutRef<'div'> {
  description: ReactNode;
  image: {
    src: string;
    srcSet: string;
    alt?: string;
  };
  isActive: boolean;
}

const TabImage = ({ description, image, isActive }: TabImageProps) => {
  const { variants, transition } = useTabContentVariants();

  return (
    <div className={styles.imgWrapper}>
      <motion.p
        className={styles.description}
        initial={'inactive'}
        animate={isActive ? 'active' : 'inactive'}
        variants={variants}
        transition={transition}
      >
        {description}
      </motion.p>
      <img src={image.src} srcSet={image.srcSet} alt={image.alt || ''} role={'presentation'} />
    </div>
  );
};

export interface CfdPlatformsSectionProps {
  title: React.ReactNode;
  className?: classNames.Argument | classNames.ArgumentArray;
}

export function CfdPlatformsNoMT5Section(props: CfdPlatformsSectionProps) {
  const tr = useTranslations();

  return (
    <section
      className={classNames(styles.accountTypesSection, props.className)}
      data-event-block-name={EventBlockNames.platforms}
    >
      <Container>
        <VStack>
          <CenterHeading>
            <AnimatedText el={'h2'} className={classNames(typography.h2)}>
              {tr.rich('pages.cfd.index.sections.platform.nomt5.title', {
                span: (_) => <AccentFragment>{_}</AccentFragment>,
              })}
            </AnimatedText>
            <p className={classNames(typography.subheadingSM, typography.headerSubhead)}>
              {tr.rich('pages.cfd.index.sections.platform.nomt5.subtitle')}
            </p>
          </CenterHeading>
          <div className={classNames(styles.placeholder)}>
            <DeprecatedTabPanel value={0} isActive={true}>
              <TabImage
                description={tr.rich('pages.cfd.index.sections.platform.tabs.imgtitleweb')}
                image={{
                  src: webtraderImageX1.src,
                  srcSet: `${webtraderImageX1.src} 1x, ${webtraderImageX2.src} 2x`,
                  alt: 'webtrader',
                }}
                isActive={true}
              />
            </DeprecatedTabPanel>
          </div>
          <div className={classNames(styles.ctaContainer)}>
            <WebtraderCta />
          </div>
        </VStack>
      </Container>
    </section>
  );
}

export function CfdPlatformsTabsSection(props: CfdPlatformsSectionProps) {
  const [selectedPlatform, setSelectedPlatform] = useState(Platform.WT);
  const tr = useTranslations();

  const subtitles = {
    [Platform.WT]: tr.rich('pages.cfd.categories.sections.platforms.subtitle'),
    [Platform.MT5]: tr.rich('pages.cfd.index.sections.platform.subtitle'),
  };

  return (
    <section
      className={classNames(styles.accountTypesSection, props.className)}
      data-event-block-name={EventBlockNames.platforms}
    >
      <Container>
        <VStack>
          <CenterHeading>
            <AnimatedText el={'h2'} className={classNames(typography.h2)}>
              {props.title}
            </AnimatedText>
            <p className={classNames(typography.subheadingSM, typography.headerSubhead)}>
              {subtitles[selectedPlatform]}
            </p>
          </CenterHeading>
          <DeprecatedTabs
            selected={selectedPlatform}
            onChange={({ value }) => setSelectedPlatform(value)}
            values={[
              {
                value: Platform.WT,
                label: (
                  <>
                    <IconGlobe /> {tr.rich('pages.cfd.index.sections.platform.tabs.webtrader')}
                  </>
                ),
              },
              {
                value: Platform.MT5,
                label: (
                  <>
                    <IconMetatrader5 /> {tr.rich('pages.cfd.index.sections.platform.tabs.metatrader')}
                  </>
                ),
              },
            ]}
            variant={'filled'}
            className={styles.tabs}
          />
          <div className={classNames(styles.placeholder)}>
            <DeprecatedTabPanel value={Platform.WT} isActive={selectedPlatform === Platform.WT}>
              <TabImage
                description={tr.rich('pages.cfd.index.sections.platform.tabs.imgtitleweb')}
                image={{
                  src: webtraderImageX1.src,
                  srcSet: `${webtraderImageX1.src} 1x, ${webtraderImageX2.src} 2x`,
                  alt: 'webtrader',
                }}
                isActive={selectedPlatform === Platform.WT}
              />
            </DeprecatedTabPanel>
            <DeprecatedTabPanel value={Platform.MT5} isActive={selectedPlatform === Platform.MT5}>
              <TabImage
                description={tr.rich('pages.cfd.index.sections.platform.tabs.imgtitle')}
                image={{
                  src: metatraderImageX1.src,
                  srcSet: `${metatraderImageX1.src} 1x, ${metatraderImageX2.src} 2x`,
                  alt: 'metatrader',
                }}
                isActive={selectedPlatform === Platform.MT5}
              />
            </DeprecatedTabPanel>
          </div>

          <div className={classNames(styles.ctaContainer)}>
            {selectedPlatform === Platform.WT ? <WebtraderCta /> : <MT5Cta />}
          </div>
        </VStack>
      </Container>
    </section>
  );
}

function WebtraderCta() {
  const tr = useTranslations();
  return (
    <div className={styles.ctaButtons}>
      <LinkSignup
        className={classNames(buttons.btn, buttons.btnPrimary, buttons.btnIconRight, buttons.btnM)}
        data-event-label={tr.rich('en.pages.cfd.index.sections.platform.cta')}
      >
        {tr.rich('pages.cfd.index.sections.platform.cta')} <ArrowRight />
      </LinkSignup>
      <LinkI18n
        href={'/cfd-trading/webtrader'}
        className={classNames(buttons.btnLink, buttons.btn, buttons.btnM, buttons.btnIconRight)}
        data-event-label={tr.rich('en.pages.cfd.index.sections.platform.learnMore')}
      >
        {tr.rich('pages.cfd.index.sections.platform.learnMore')}
      </LinkI18n>
    </div>
  );
}

function MT5Cta() {
  const tr = useTranslations();
  return (
    <>
      <div className={styles.ctaButtons}>
        <MT5Link
          className={classNames(buttons.btn, buttons.btnPrimary, buttons.btnIconRight, buttons.btnM)}
          blockName={EventBlockNames.platforms}
          eventLabel={'get_started'}
          platform={'web'}
        >
          {tr.rich('pages.cfd.index.sections.platform.cta')} <ArrowRight />
        </MT5Link>
        <LinkI18n
          href={'/cfd-trading/mt5'}
          className={classNames(buttons.btnLink, buttons.btn, buttons.btnM, buttons.btnIconRight)}
          data-event-label={tr.rich('en.pages.cfd.index.sections.platform.learnMore')}
        >
          {tr.rich('pages.cfd.index.sections.platform.learnMore')}
        </LinkI18n>
      </div>
      {MT5_VIA_PXBT ? (
        <Disclaimer className={styles.disclaimer}>
          {tr.rich('pages.cfd.index.sections.platform.disclaimer')}
        </Disclaimer>
      ) : null}
    </>
  );
}
